import React, { useState, useEffect } from "react";
import "../../css/TransportVehicles.css";
import {
    Search,
    Plus,
    Truck,
    Calendar,
    User,
    Settings,
    Filter,
    Edit3,
    Trash2,
    X,
    AlertTriangle,
    FileText,
    Wrench, // Replaced Tool with Wrench
    Clock,
    Droplet,
    Activity,
    Check,
    Clipboard,
    Star,
    Tag,
    
  } from "lucide-react";
import Loader from "../../components/Loader";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function TransportVehicles() {
  // State variables
  const [vehicles, setVehicles] = useState([]);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [sortBy, setSortBy] = useState("regNumber");
  const [isAddingVehicle, setIsAddingVehicle] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [viewVehicleDetails, setViewVehicleDetails] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    isOpen: false,
    vehicleId: null,
  });
  const [isAddingMaintenance, setIsAddingMaintenance] = useState(false);
  const [maintenanceRecords, setMaintenanceRecords] = useState([]);

  // New vehicle state
  const [newVehicle, setNewVehicle] = useState({
    regNumber: "",
    vehicleName: "",
    vehicleType: "bus",
    model: "",
    manufacturer: "",
    yearOfManufacture: "",
    capacity: "",
    fuelType: "diesel",
    assignedDriver: "",
    driverContact: "",
    insuranceNumber: "",
    insuranceExpiryDate: "",
    lastInspectionDate: "",
    nextInspectionDate: "",
    status: "active",
    notes: "",
    fuelEfficiency: "",
  });

  // New maintenance record state
  const [newMaintenance, setNewMaintenance] = useState({
    date: "",
    type: "routine",
    description: "",
    cost: "",
    performedBy: "",
    notes: "",
  });


  // Stats calculation
  const vehicleStats = {
    total: vehicles.length,
    active: vehicles.filter((v) => v.status === "active").length,
    maintenance: vehicles.filter((v) => v.status === "maintenance").length,
    inactive: vehicles.filter((v) => v.status === "inactive").length,
  };

  // Form handlers
  const handleVehicleChange = (e) => {
    const { name, value } = e.target;
    setNewVehicle({
      ...newVehicle,
      [name]: value,
    });
  };

  const handleMaintenanceChange = (e) => {
    const { name, value } = e.target;
    setNewMaintenance({
      ...newMaintenance,
      [name]: value,
    });
  };

  // Simulating API calls for CRUD operations
  const fetchVehicles = async () => {
    // This would be a real API call in production
    setIsLoading(true);
    setTimeout(() => {
      // This simulates the API response
      setIsLoading(false);
    }, 500);
  };

  const addNewVehicle = async () => {
    // In a real implementation, this would be an API POST request
    setIsLoading(true);
    setTimeout(() => {
      const newId = `v${vehicles.length + 1}`;
      const vehicleToAdd = {
        id: newId,
        ...newVehicle,
        maintenanceRecords: [],
      };
      setVehicles([...vehicles, vehicleToAdd]);
      setIsLoading(false);
      toast.success("Vehicle added successfully!");
      resetVehicleForm();
    }, 500);
  };

  const updateVehicle = async () => {
    // In a real implementation, this would be an API PUT request
    setIsLoading(true);
    setTimeout(() => {
      const updatedVehicles = vehicles.map((vehicle) =>
        vehicle.id === selectedVehicle.id
          ? {
              ...vehicle,
              ...newVehicle,
              maintenanceRecords: vehicle.maintenanceRecords,
            }
          : vehicle
      );
      setVehicles(updatedVehicles);
      setIsLoading(false);
      toast.success("Vehicle updated successfully!");
      resetVehicleForm();
    }, 500);
  };

  const addMaintenanceRecord = async () => {
    // In a real implementation, this would be an API POST request
    if (!viewVehicleDetails) return;

    setIsLoading(true);
    setTimeout(() => {
      const newId = `m${Math.floor(Math.random() * 1000)}`;
      const newRecord = {
        id: newId,
        ...newMaintenance,
      };

      const updatedVehicles = vehicles.map((vehicle) =>
        vehicle.id === viewVehicleDetails.id
          ? {
              ...vehicle,
              maintenanceRecords: [...vehicle.maintenanceRecords, newRecord],
            }
          : vehicle
      );

      setVehicles(updatedVehicles);
      const updatedVehicle = updatedVehicles.find(
        (v) => v.id === viewVehicleDetails.id
      );
      setViewVehicleDetails(updatedVehicle);
      setIsLoading(false);
      setIsAddingMaintenance(false);
      resetMaintenanceForm();
      toast.success("Maintenance record added successfully!");
    }, 500);
  };

  const deleteVehicle = async (id) => {
    // In a real implementation, this would be an API DELETE request
    setIsLoading(true);
    setTimeout(() => {
      const updatedVehicles = vehicles.filter((vehicle) => vehicle.id !== id);
      setVehicles(updatedVehicles);
      setIsLoading(false);
      setDeleteConfirmation({ isOpen: false, vehicleId: null });
      toast.success("Vehicle deleted successfully!");
    }, 500);
  };

  const toggleVehicleStatus = (id) => {
    // In a real implementation, this would be an API PATCH request
    const vehicle = vehicles.find((v) => v.id === id);
    if (!vehicle) return;

    let newStatus;
    switch (vehicle.status) {
      case "active":
        newStatus = "inactive";
        break;
      case "inactive":
        newStatus = "maintenance";
        break;
      case "maintenance":
        newStatus = "active";
        break;
      default:
        newStatus = "active";
    }

    const updatedVehicles = vehicles.map((v) =>
      v.id === id ? { ...v, status: newStatus } : v
    );
    setVehicles(updatedVehicles);
    toast.success(`Vehicle status changed to ${newStatus}`);
  };

  // Form submission handlers
  const handleVehicleSubmit = (e) => {
    e.preventDefault();

    if (selectedVehicle) {
      // Edit existing vehicle
      updateVehicle();
    } else {
      // Add new vehicle
      addNewVehicle();
    }
  };

  const handleMaintenanceSubmit = (e) => {
    e.preventDefault();
    addMaintenanceRecord();
  };

  // UI handlers
  const openVehicleDetails = (vehicle) => {
    setViewVehicleDetails(vehicle);
  };

  const closeVehicleDetails = () => {
    setViewVehicleDetails(null);
    setIsAddingMaintenance(false);
  };

  const handleEditVehicle = (vehicle) => {
    setSelectedVehicle(vehicle);
    setNewVehicle({
      regNumber: vehicle.regNumber,
      vehicleName: vehicle.vehicleName,
      vehicleType: vehicle.vehicleType,
      model: vehicle.model,
      manufacturer: vehicle.manufacturer,
      yearOfManufacture: vehicle.yearOfManufacture,
      capacity: vehicle.capacity,
      fuelType: vehicle.fuelType,
      assignedDriver: vehicle.assignedDriver,
      driverContact: vehicle.driverContact,
      insuranceNumber: vehicle.insuranceNumber,
      insuranceExpiryDate: vehicle.insuranceExpiryDate,
      lastInspectionDate: vehicle.lastInspectionDate,
      nextInspectionDate: vehicle.nextInspectionDate,
      status: vehicle.status,
      notes: vehicle.notes,
      fuelEfficiency: vehicle.fuelEfficiency,
    });
    setIsAddingVehicle(true);
    closeVehicleDetails();
  };

  const handleDeleteConfirmation = (id) => {
    setDeleteConfirmation({
      isOpen: true,
      vehicleId: id,
    });
  };

  const confirmDelete = () => {
    deleteVehicle(deleteConfirmation.vehicleId);
  };

  const cancelDelete = () => {
    setDeleteConfirmation({
      isOpen: false,
      vehicleId: null,
    });
  };

  const resetVehicleForm = () => {
    setNewVehicle({
      regNumber: "",
      vehicleName: "",
      vehicleType: "bus", // Default value
      model: "",
      manufacturer: "",
      yearOfManufacture: "",
      capacity: "",
      fuelType: "diesel", // Default value
      assignedDriver: "",
      driverContact: "",
      insuranceNumber: "",
      insuranceExpiryDate: "",
      lastInspectionDate: "",
      nextInspectionDate: "",
      status: "active", // Default value
      notes: "",
      fuelEfficiency: "",
    });
    setSelectedVehicle(null);
    setIsAddingVehicle(false);
  };

  const resetMaintenanceForm = () => {
    setNewMaintenance({
      date: "",
      type: "routine", // Default value
      description: "",
      cost: "",
      performedBy: "",
      notes: "",
    });
  };

  // Filter vehicles based on search term and filters
  const filteredVehicles = vehicles.filter((vehicle) => {
    // Search term filter
    const matchesSearch =
      vehicle.regNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.vehicleName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.assignedDriver.toLowerCase().includes(searchTerm.toLowerCase());

    // Status filter
    const matchesStatus =
      filterStatus === "all" || vehicle.status === filterStatus;

    // Vehicle type filter
    const matchesType =
      filterType === "all" || vehicle.vehicleType === filterType;

    return matchesSearch && matchesStatus && matchesType;
  });

  // Get days until insurance expiry
  const getDaysUntilExpiry = (dateString) => {
    const expiryDate = new Date(dateString);
    const today = new Date();
    const differenceInTime = expiryDate.getTime() - today.getTime();
    const differenceInDays = Math.ceil(differenceInTime / (1000 * 3600 * 24));
    return differenceInDays;
  };

  // Get class for expiry warning
  const getExpiryWarningClass = (dateString) => {
    const days = getDaysUntilExpiry(dateString);
    if (days < 0) return "transportVehicles-expired";
    if (days <= 30) return "transportVehicles-expiring-soon";
    return "";
  };

  // Format date for display
  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  return (
    <div className="transportVehicles-container">
      {/* Header Section */}
      <div className="transportVehicles-header">
        <div className="transportVehicles-title-container">
          <div className="transportVehicles-icon">
            <Truck size={28} />
          </div>
          <h1 className="transportVehicles-title">
            Transport Vehicles Management
          </h1>
        </div>

        <div className="transportVehicles-controls">
          <div className="transportVehicles-search-container">
            <Search className="transportVehicles-search-icon" size={18} />
            <input
              type="text"
              className="transportVehicles-search-input"
              placeholder="Search by registration, name or driver..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <button
            className="transportVehicles-filter-toggle-btn"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={16} />
            Filters
          </button>

          <button
            className="transportVehicles-add-btn"
            onClick={() => setIsAddingVehicle(!isAddingVehicle)}
          >
            <Plus size={16} />
            {isAddingVehicle ? "Cancel" : "Add New Vehicle"}
          </button>
        </div>

        {showFilters && (
          <div className="transportVehicles-filter-options">
            <div className="transportVehicles-filter-group">
              <label>Status:</label>
              <select
                className="transportVehicles-filter-select"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="maintenance">Under Maintenance</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div className="transportVehicles-filter-group">
              <label>Vehicle Type:</label>
              <select
                className="transportVehicles-filter-select"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="all">All Types</option>
                <option value="bus">Bus</option>
                <option value="van">Van</option>
                <option value="car">Car</option>
                <option value="truck">Truck</option>
              </select>
            </div>

            <div className="transportVehicles-filter-group">
              <label>Sort By:</label>
              <select
                className="transportVehicles-filter-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="regNumber">Registration Number</option>
                <option value="vehicleName">Vehicle Name</option>
                <option value="yearOfManufacture">Year of Manufacture</option>
                <option value="nextInspectionDate">Next Inspection Date</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Stats Section */}
      <div className="transportVehicles-stats">
        <div className="transportVehicles-stat-box">
          <span className="transportVehicles-stat-number">
            {vehicleStats.total}
          </span>
          <span className="transportVehicles-stat-label">Total Vehicles</span>
        </div>
        <div className="transportVehicles-stat-box active">
          <span className="transportVehicles-stat-number">
            {vehicleStats.active}
          </span>
          <span className="transportVehicles-stat-label">Active Vehicles</span>
        </div>
        <div className="transportVehicles-stat-box maintenance">
          <span className="transportVehicles-stat-number">
            {vehicleStats.maintenance}
          </span>
          <span className="transportVehicles-stat-label">
            Under Maintenance
          </span>
        </div>
        <div className="transportVehicles-stat-box inactive">
          <span className="transportVehicles-stat-number">
            {vehicleStats.inactive}
          </span>
          <span className="transportVehicles-stat-label">
            Inactive Vehicles
          </span>
        </div>
      </div>

      {isLoading && (
        <div className="transportVehicles-loader-container">
          <Loader />
        </div>
      )}

      {/* Form Section for Adding/Editing Vehicles */}
      {isAddingVehicle ? (
        <div className="transportVehicles-form-container">
          <div className="transportVehicles-form-header">
            <h2>
              {selectedVehicle ? "Edit Vehicle Details" : "Add New Vehicle"}
            </h2>
          </div>

          <form
            onSubmit={handleVehicleSubmit}
            className="transportVehicles-form"
          >
            <div className="transportVehicles-form-section">
              <h3>Basic Information</h3>
              <div className="transportVehicles-form-row">
                <div className="transportVehicles-form-group">
                  <label htmlFor="regNumber">Registration Number*</label>
                  <input
                    type="text"
                    id="regNumber"
                    name="regNumber"
                    value={newVehicle.regNumber}
                    onChange={handleVehicleChange}
                    required
                    placeholder="e.g. KA-01-F-5454"
                  />
                </div>

                <div className="transportVehicles-form-group">
                  <label htmlFor="vehicleName">Vehicle Name*</label>
                  <input
                    type="text"
                    id="vehicleName"
                    name="vehicleName"
                    value={newVehicle.vehicleName}
                    onChange={handleVehicleChange}
                    required
                    placeholder="e.g. School Bus 1"
                  />
                </div>
              </div>

              <div className="transportVehicles-form-row">
                <div className="transportVehicles-form-group">
                  <label htmlFor="vehicleType">Vehicle Type*</label>
                  <select
                    id="vehicleType"
                    name="vehicleType"
                    value={newVehicle.vehicleType}
                    onChange={handleVehicleChange}
                    required
                  >
                    <option value="bus">Bus</option>
                    <option value="van">Van</option>
                    <option value="car">Car</option>
                    <option value="truck">Truck</option>
                  </select>
                </div>

                <div className="transportVehicles-form-group">
                  <label htmlFor="status">Status*</label>
                  <select
                    id="status"
                    name="status"
                    value={newVehicle.status}
                    onChange={handleVehicleChange}
                    required
                  >
                    <option value="active">Active</option>
                    <option value="maintenance">Under Maintenance</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="transportVehicles-form-section">
              <h3>Vehicle Details</h3>
              <div className="transportVehicles-form-row">
                <div className="transportVehicles-form-group">
                  <label htmlFor="manufacturer">Manufacturer*</label>
                  <input
                    type="text"
                    id="manufacturer"
                    name="manufacturer"
                    value={newVehicle.manufacturer}
                    onChange={handleVehicleChange}
                    required
                    placeholder="e.g. Ashok Leyland"
                  />
                </div>

                <div className="transportVehicles-form-group">
                  <label htmlFor="model">Model*</label>
                  <input
                    type="text"
                    id="model"
                    name="model"
                    value={newVehicle.model}
                    onChange={handleVehicleChange}
                    required
                    placeholder="e.g. Viking"
                  />
                </div>
              </div>

              <div className="transportVehicles-form-row">
                <div className="transportVehicles-form-group">
                  <label htmlFor="yearOfManufacture">
                    Year of Manufacture*
                  </label>
                  <input
                    type="text"
                    id="yearOfManufacture"
                    name="yearOfManufacture"
                    value={newVehicle.yearOfManufacture}
                    onChange={handleVehicleChange}
                    required
                    placeholder="e.g. 2020"
                  />
                </div>

                <div className="transportVehicles-form-group">
                  <label htmlFor="capacity">Seating Capacity*</label>
                  <input
                    type="text"
                    id="capacity"
                    name="capacity"
                    value={newVehicle.capacity}
                    onChange={handleVehicleChange}
                    required
                    placeholder="e.g. 52"
                  />
                </div>
              </div>

              <div className="transportVehicles-form-row">
                <div className="transportVehicles-form-group">
                  <label htmlFor="fuelType">Fuel Type*</label>
                  <select
                    id="fuelType"
                    name="fuelType"
                    value={newVehicle.fuelType}
                    onChange={handleVehicleChange}
                    required
                  >
                    <option value="diesel">Diesel</option>
                    <option value="petrol">Petrol</option>
                    <option value="cng">CNG</option>
                    <option value="electric">Electric</option>
                    <option value="hybrid">Hybrid</option>
                  </select>
                </div>

                <div className="transportVehicles-form-group">
                  <label htmlFor="fuelEfficiency">Fuel Efficiency</label>
                  <input
                    type="text"
                    id="fuelEfficiency"
                    name="fuelEfficiency"
                    value={newVehicle.fuelEfficiency}
                    onChange={handleVehicleChange}
                    placeholder="e.g. 8 km/l"
                  />
                </div>
              </div>
            </div>

            <div className="transportVehicles-form-section">
              <h3>Driver Information</h3>
              <div className="transportVehicles-form-row">
                <div className="transportVehicles-form-group">
                  <label htmlFor="assignedDriver">Assigned Driver*</label>
                  <input
                    type="text"
                    id="assignedDriver"
                    name="assignedDriver"
                    value={newVehicle.assignedDriver}
                    onChange={handleVehicleChange}
                    required
                    placeholder="e.g. Rajesh Kumar"
                  />
                </div>

                <div className="transportVehicles-form-group">
                  <label htmlFor="driverContact">Driver Contact*</label>
                  <input
                    type="text"
                    id="driverContact"
                    name="driverContact"
                    value={newVehicle.driverContact}
                    onChange={handleVehicleChange}
                    required
                    placeholder="e.g. 9876543210"
                  />
                </div>
              </div>
            </div>

            <div className="transportVehicles-form-section">
              <h3>Documentation & Inspection</h3>
              <div className="transportVehicles-form-row">
                <div className="transportVehicles-form-group">
                  <label htmlFor="insuranceNumber">Insurance Number*</label>
                  <input
                    type="text"
                    id="insuranceNumber"
                    name="insuranceNumber"
                    value={newVehicle.insuranceNumber}
                    onChange={handleVehicleChange}
                    required
                    placeholder="e.g. INS123456"
                  />
                </div>

                <div className="transportVehicles-form-group">
                  <label htmlFor="insuranceExpiryDate">
                    Insurance Expiry Date*
                  </label>
                  <input
                    type="date"
                    id="insuranceExpiryDate"
                    name="insuranceExpiryDate"
                    value={newVehicle.insuranceExpiryDate}
                    onChange={handleVehicleChange}
                    required
                  />
                </div>
              </div>

              <div className="transportVehicles-form-row">
                <div className="transportVehicles-form-group">
                  <label htmlFor="lastInspectionDate">
                    Last Inspection Date*
                  </label>
                  <input
                    type="date"
                    id="lastInspectionDate"
                    name="lastInspectionDate"
                    value={newVehicle.lastInspectionDate}
                    onChange={handleVehicleChange}
                    required
                  />
                </div>

                <div className="transportVehicles-form-row">
                  <div className="transportVehicles-form-group">
                    <label htmlFor="nextInspectionDate">
                      Next Inspection Date*
                    </label>
                    <input
                      type="date"
                      id="nextInspectionDate"
                      name="nextInspectionDate"
                      value={newVehicle.nextInspectionDate}
                      onChange={handleVehicleChange}
                      required
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="transportVehicles-form-section">
              <h3>Additional Information</h3>
              <div className="transportVehicles-form-group">
                <label htmlFor="notes">Notes</label>
                <textarea
                  id="notes"
                  name="notes"
                  value={newVehicle.notes}
                  onChange={handleVehicleChange}
                  placeholder="Any additional information about the vehicle..."
                ></textarea>
              </div>
            </div>

            <div className="transportVehicles-form-actions">
              <button
                type="button"
                className="transportVehicles-cancel-btn"
                onClick={resetVehicleForm}
              >
                Cancel
              </button>
              <button type="submit" className="transportVehicles-submit-btn">
                {selectedVehicle ? "Update Vehicle" : "Add Vehicle"}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <>
          {/* Vehicle Listing Section */}
          <div className="transportVehicles-list-container">
            <h2 className="transportVehicles-section-title">Vehicle List</h2>
            {filteredVehicles.length === 0 ? (
              <div className="transportVehicles-no-data">
                <AlertTriangle size={24} />
                <p>No vehicles found matching your search criteria.</p>
              </div>
            ) : (
              <div className="transportVehicles-table-container">
                <table className="transportVehicles-table">
                  <thead>
                    <tr>
                      <th>Reg. Number</th>
                      <th>Vehicle Name</th>
                      <th>Type</th>
                      <th>Driver</th>
                      <th>Status</th>
                      <th>Insurance Expiry</th>
                      <th>Next Inspection</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredVehicles.map((vehicle) => (
                      <tr key={vehicle.id}>
                        <td>{vehicle.regNumber}</td>
                        <td>{vehicle.vehicleName}</td>
                        <td>
                          <span
                            className={`transportVehicles-type transportVehicles-type-${vehicle.vehicleType}`}
                          >
                            {vehicle.vehicleType.charAt(0).toUpperCase() +
                              vehicle.vehicleType.slice(1)}
                          </span>
                        </td>
                        <td>{vehicle.assignedDriver}</td>
                        <td>
                          <span
                            className={`transportVehicles-status transportVehicles-status-${vehicle.status}`}
                            onClick={() => toggleVehicleStatus(vehicle.id)}
                          >
                            {vehicle.status.charAt(0).toUpperCase() +
                              vehicle.status.slice(1)}
                          </span>
                        </td>
                        <td
                          className={getExpiryWarningClass(
                            vehicle.insuranceExpiryDate
                          )}
                        >
                          {formatDate(vehicle.insuranceExpiryDate)}
                        </td>
                        <td
                          className={getExpiryWarningClass(
                            vehicle.nextInspectionDate
                          )}
                        >
                          {formatDate(vehicle.nextInspectionDate)}
                        </td>
                        <td className="transportVehicles-actions">
                          <button
                            className="transportVehicles-btn-view"
                            onClick={() => openVehicleDetails(vehicle)}
                            title="View Details"
                          >
                            <FileText size={16} />
                          </button>
                          <button
                            className="transportVehicles-btn-edit"
                            onClick={() => handleEditVehicle(vehicle)}
                            title="Edit Vehicle"
                          >
                            <Edit3 size={16} />
                          </button>
                          <button
                            className="transportVehicles-btn-delete"
                            onClick={() => handleDeleteConfirmation(vehicle.id)}
                            title="Delete Vehicle"
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Vehicle Details Modal */}
      {viewVehicleDetails && (
        <div className="transportVehicles-modal-overlay">
          <div className="transportVehicles-modal">
            <div className="transportVehicles-modal-header">
              <h2>{viewVehicleDetails.vehicleName}</h2>
              <button
                className="transportVehicles-modal-close"
                onClick={closeVehicleDetails}
              >
                <X size={20} />
              </button>
            </div>

            <div className="transportVehicles-modal-content">
              <div className="transportVehicles-modal-section">
                <h3>Basic Information</h3>
                <div className="transportVehicles-detail-row">
                  <div className="transportVehicles-detail-item">
                    <span className="transportVehicles-detail-label">
                      Registration Number:
                    </span>
                    <span className="transportVehicles-detail-value">
                      {viewVehicleDetails.regNumber}
                    </span>
                  </div>
                  <div className="transportVehicles-detail-item">
                    <span className="transportVehicles-detail-label">
                      Vehicle Type:
                    </span>
                    <span className="transportVehicles-detail-value">
                      <span
                        className={`transportVehicles-type transportVehicles-type-${viewVehicleDetails.vehicleType}`}
                      >
                        {viewVehicleDetails.vehicleType
                          .charAt(0)
                          .toUpperCase() +
                          viewVehicleDetails.vehicleType.slice(1)}
                      </span>
                    </span>
                  </div>
                </div>

                <div className="transportVehicles-detail-row">
                  <div className="transportVehicles-detail-item">
                    <span className="transportVehicles-detail-label">
                      Model:
                    </span>
                    <span className="transportVehicles-detail-value">
                      {viewVehicleDetails.model}
                    </span>
                  </div>
                  <div className="transportVehicles-detail-item">
                    <span className="transportVehicles-detail-label">
                      Manufacturer:
                    </span>
                    <span className="transportVehicles-detail-value">
                      {viewVehicleDetails.manufacturer}
                    </span>
                  </div>
                </div>

                <div className="transportVehicles-detail-row">
                  <div className="transportVehicles-detail-item">
                    <span className="transportVehicles-detail-label">
                      Year of Manufacture:
                    </span>
                    <span className="transportVehicles-detail-value">
                      {viewVehicleDetails.yearOfManufacture}
                    </span>
                  </div>
                  <div className="transportVehicles-detail-item">
                    <span className="transportVehicles-detail-label">
                      Seating Capacity:
                    </span>
                    <span className="transportVehicles-detail-value">
                      {viewVehicleDetails.capacity}
                    </span>
                  </div>
                </div>

                <div className="transportVehicles-detail-row">
                  <div className="transportVehicles-detail-item">
                    <span className="transportVehicles-detail-label">
                      Fuel Type:
                    </span>
                    <span className="transportVehicles-detail-value">
                      {viewVehicleDetails.fuelType.charAt(0).toUpperCase() +
                        viewVehicleDetails.fuelType.slice(1)}
                    </span>
                  </div>
                  <div className="transportVehicles-detail-item">
                    <span className="transportVehicles-detail-label">
                      Fuel Efficiency:
                    </span>
                    <span className="transportVehicles-detail-value">
                      {viewVehicleDetails.fuelEfficiency}
                    </span>
                  </div>
                </div>

                <div className="transportVehicles-detail-row">
                  <div className="transportVehicles-detail-item">
                    <span className="transportVehicles-detail-label">
                      Status:
                    </span>
                    <span className="transportVehicles-detail-value">
                      <span
                        className={`transportVehicles-status transportVehicles-status-${viewVehicleDetails.status}`}
                      >
                        {viewVehicleDetails.status.charAt(0).toUpperCase() +
                          viewVehicleDetails.status.slice(1)}
                      </span>
                    </span>
                  </div>
                </div>
              </div>

              <div className="transportVehicles-modal-section">
                <h3>Driver Information</h3>
                <div className="transportVehicles-detail-row">
                  <div className="transportVehicles-detail-item">
                    <span className="transportVehicles-detail-label">
                      Driver Name:
                    </span>
                    <span className="transportVehicles-detail-value">
                      {viewVehicleDetails.assignedDriver}
                    </span>
                  </div>
                  <div className="transportVehicles-detail-item">
                    <span className="transportVehicles-detail-label">
                      Contact Number:
                    </span>
                    <span className="transportVehicles-detail-value">
                      {viewVehicleDetails.driverContact}
                    </span>
                  </div>
                </div>
              </div>

              <div className="transportVehicles-modal-section">
                <h3>Documentation & Inspection</h3>
                <div className="transportVehicles-detail-row">
                  <div className="transportVehicles-detail-item">
                    <span className="transportVehicles-detail-label">
                      Insurance Number:
                    </span>
                    <span className="transportVehicles-detail-value">
                      {viewVehicleDetails.insuranceNumber}
                    </span>
                  </div>
                  <div className="transportVehicles-detail-item">
                    <span className="transportVehicles-detail-label">
                      Insurance Expiry:
                    </span>
                    <span
                      className={`transportVehicles-detail-value ${getExpiryWarningClass(
                        viewVehicleDetails.insuranceExpiryDate
                      )}`}
                    >
                      {formatDate(viewVehicleDetails.insuranceExpiryDate)}
                    </span>
                  </div>
                </div>

                <div className="transportVehicles-detail-row">
                  <div className="transportVehicles-detail-item">
                    <span className="transportVehicles-detail-label">
                      Last Inspection:
                    </span>
                    <span className="transportVehicles-detail-value">
                      {formatDate(viewVehicleDetails.lastInspectionDate)}
                    </span>
                  </div>
                  <div className="transportVehicles-detail-item">
                    <span className="transportVehicles-detail-label">
                      Next Inspection:
                    </span>
                    <span
                      className={`transportVehicles-detail-value ${getExpiryWarningClass(
                        viewVehicleDetails.nextInspectionDate
                      )}`}
                    >
                      {formatDate(viewVehicleDetails.nextInspectionDate)}
                    </span>
                  </div>
                </div>
              </div>

              {viewVehicleDetails.notes && (
                <div className="transportVehicles-modal-section">
                  <h3>Additional Notes</h3>
                  <div className="transportVehicles-notes">
                    {viewVehicleDetails.notes}
                  </div>
                </div>
              )}

              <div className="transportVehicles-modal-section">
                <div className="transportVehicles-maintenance-header">
                  <h3>Maintenance Records</h3>
                  <button
                    className="transportVehicles-maintenance-add-btn"
                    onClick={() => setIsAddingMaintenance(!isAddingMaintenance)}
                  >
                    <Plus size={16} />
                    {isAddingMaintenance ? "Cancel" : "Add Maintenance Record"}
                  </button>
                </div>

                {isAddingMaintenance && (
                  <div className="transportVehicles-maintenance-form">
                    <h4>Add New Maintenance Record</h4>
                    <form onSubmit={handleMaintenanceSubmit}>
                      <div className="transportVehicles-form-row">
                        <div className="transportVehicles-form-group">
                          <label htmlFor="date">Date*</label>
                          <input
                            type="date"
                            id="date"
                            name="date"
                            value={newMaintenance.date}
                            onChange={handleMaintenanceChange}
                            required
                          />
                        </div>

                        <div className="transportVehicles-form-group">
                          <label htmlFor="type">Maintenance Type*</label>
                          <select
                            id="type"
                            name="type"
                            value={newMaintenance.type}
                            onChange={handleMaintenanceChange}
                            required
                          >
                            <option value="routine">Routine Service</option>
                            <option value="repair">Repair</option>
                            <option value="inspection">Inspection</option>
                            <option value="accident">Accident Repair</option>
                            <option value="other">Other</option>
                          </select>
                        </div>
                      </div>

                      <div className="transportVehicles-form-row">
                        <div className="transportVehicles-form-group">
                          <label htmlFor="description">Description*</label>
                          <input
                            type="text"
                            id="description"
                            name="description"
                            value={newMaintenance.description}
                            onChange={handleMaintenanceChange}
                            required
                            placeholder="e.g. Oil Change and General Service"
                          />
                        </div>

                        <div className="transportVehicles-form-group">
                          <label htmlFor="cost">Cost (₹)*</label>
                          <input
                            type="text"
                            id="cost"
                            name="cost"
                            value={newMaintenance.cost}
                            onChange={handleMaintenanceChange}
                            required
                            placeholder="e.g. 4500"
                          />
                        </div>
                      </div>

                      <div className="transportVehicles-form-row">
                        <div className="transportVehicles-form-group">
                          <label htmlFor="performedBy">Performed By*</label>
                          <input
                            type="text"
                            id="performedBy"
                            name="performedBy"
                            value={newMaintenance.performedBy}
                            onChange={handleMaintenanceChange}
                            required
                            placeholder="e.g. City Automobile Service Center"
                          />
                        </div>
                      </div>

                      <div className="transportVehicles-form-row">
                        <div className="transportVehicles-form-group">
                          <label htmlFor="maintNotes">Notes</label>
                          <textarea
                            id="maintNotes"
                            name="notes"
                            value={newMaintenance.notes}
                            onChange={handleMaintenanceChange}
                            placeholder="Any additional information..."
                          ></textarea>
                        </div>
                      </div>

                      <div className="transportVehicles-form-actions">
                        <button
                          type="button"
                          className="transportVehicles-cancel-btn"
                          onClick={() => setIsAddingMaintenance(false)}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="transportVehicles-submit-btn"
                        >
                          Add Record
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {viewVehicleDetails.maintenanceRecords &&
                viewVehicleDetails.maintenanceRecords.length > 0 ? (
                  <div className="transportVehicles-maintenance-records">
                    {viewVehicleDetails.maintenanceRecords.map((record) => (
                      <div
                        key={record.id}
                        className={`transportVehicles-maintenance-card transportVehicles-maintenance-${record.type}`}
                      >
                        <div className="transportVehicles-maintenance-header">
                          <div className="transportVehicles-maintenance-type">
                            {record.type === "routine" && <Settings size={18} />}
                            {record.type === "repair" && <Settings size={18} />}
                            {record.type === "inspection" && (
                              <Clipboard size={18} />
                            )}
                            {record.type === "accident" && (
                              <AlertTriangle size={18} />
                            )}
                            {record.type === "other" && <Tag size={18} />}
                            <span>
                              {record.type.charAt(0).toUpperCase() +
                                record.type.slice(1)}
                            </span>
                          </div>
                          <div className="transportVehicles-maintenance-date">
                            <Calendar size={18} />
                            <span>{formatDate(record.date)}</span>
                          </div>
                        </div>
                        <div className="transportVehicles-maintenance-body">
                          <h4>{record.description}</h4>
                          <div className="transportVehicles-maintenance-details">
                            <div className="transportVehicles-maintenance-detail">
                              <User size={16} />
                              <span>{record.performedBy}</span>
                            </div>
                            <div className="transportVehicles-maintenance-detail">
                              <Tag size={16} />
                              <span>₹{record.cost}</span>
                            </div>
                          </div>
                          {record.notes && (
                            <div className="transportVehicles-maintenance-notes">
                              <strong>Notes:</strong> {record.notes}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="transportVehicles-no-data">
                    <Clock size={24} />
                    <p>No maintenance records available for this vehicle.</p>
                  </div>
                )}
              </div>
            </div>

            <div className="transportVehicles-modal-actions">
              <button
                className="transportVehicles-action-btn"
                onClick={() => handleEditVehicle(viewVehicleDetails)}
              >
                <Edit3 size={16} /> Edit Details
              </button>
              <button
                className="transportVehicles-action-btn"
                onClick={() => handleDeleteConfirmation(viewVehicleDetails.id)}
              >
                <Trash2 size={16} /> Delete Vehicle
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmation.isOpen && (
        <div className="transportVehicles-modal-overlay">
          <div className="transportVehicles-confirm-modal">
            <div className="transportVehicles-confirm-header">
              <AlertTriangle size={24} />
              <h3>Confirm Deletion</h3>
            </div>
            <div className="transportVehicles-confirm-content">
              <p>
                Are you sure you want to delete this vehicle? This action cannot
                be undone.
              </p>
            </div>
            <div className="transportVehicles-confirm-actions">
              <button
                className="transportVehicles-cancel-btn"
                onClick={cancelDelete}
              >
                Cancel
              </button>
              <button
                className="transportVehicles-delete-btn"
                onClick={confirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
}

export default TransportVehicles;
